import type { Mode, TemplateSettings } from "../lib/types";
import { MODE_PROFILES, settingsForMode } from "../lib/types";
import "./SettingsPanel.css";

interface Props {
  settings: TemplateSettings;
  onChange: (patch: Partial<TemplateSettings> | TemplateSettings) => void;
  title?: string;
  compact?: boolean;
}

export default function SettingsPanel({ settings, onChange, title, compact }: Props) {
  const profile = MODE_PROFILES[settings.mode];

  const handleModeChange = (mode: Mode) => {
    if (mode === settings.mode) return;
    // Switching mode resets grid/palette/cellPx to mode-appropriate defaults so the
    // user isn't left with a 25-cell crochet grid silently used for cross-stitch.
    onChange(settingsForMode(mode, { showSymbols: settings.showSymbols, mergeDistance: settings.mergeDistance }));
  };

  return (
    <div className={`settings${compact ? " compact" : ""}`}>
      {title && <h3>{title}</h3>}

      <div className="setting mode-toggle">
        <span>Project type</span>
        <div className="mode-buttons" role="radiogroup" aria-label="Project type">
          {(Object.keys(MODE_PROFILES) as Mode[]).map((m) => (
            <button
              key={m}
              type="button"
              role="radio"
              aria-checked={settings.mode === m}
              className={`mode-button${settings.mode === m ? " active" : ""}`}
              onClick={() => handleModeChange(m)}
            >
              {MODE_PROFILES[m].label}
            </button>
          ))}
        </div>
      </div>

      <label className="setting">
        <span>Grid size: <strong>{settings.grid}×{settings.grid}</strong></span>
        <input
          type="range"
          min={profile.gridMin}
          max={profile.gridMax}
          step={profile.gridStep}
          value={settings.grid}
          onChange={(e) => onChange({ grid: Number(e.target.value) })}
        />
      </label>

      <label className="setting">
        <span>Max colors: <strong>{settings.paletteSize}</strong></span>
        <input
          type="range"
          min={profile.paletteMin}
          max={profile.paletteMax}
          step={1}
          value={settings.paletteSize}
          onChange={(e) => onChange({ paletteSize: Number(e.target.value) })}
        />
      </label>

      <label className="setting">
        <span>
          Merge similar colors: <strong>{settings.mergeDistance}</strong>
        </span>
        <input
          type="range"
          min={0}
          max={60}
          step={1}
          value={settings.mergeDistance}
          onChange={(e) => onChange({ mergeDistance: Number(e.target.value) })}
        />
        <span className="muted small">
          Higher = combine close colors into one yarn or floss.
        </span>
      </label>

      <label className="setting checkbox">
        <input
          type="checkbox"
          checked={settings.showSymbols}
          onChange={(e) => onChange({ showSymbols: e.target.checked })}
        />
        <span>Show symbols in cells (helpful when colors look alike)</span>
      </label>
    </div>
  );
}
